import { findLastIndex, repeat } from "./helpers.js";
import parse_mask from "./parse-mask.js";
class MaskUtils {
    constructor(options){
        this.isCharacterAllowedAtPosition = (character, position)=>{
            const options = this.maskOptions;
            if (!options) return false;
            const { maskPlaceholder } = options;
            if (this.isCharacterFillingPosition(character, position)) return true;
            if (!maskPlaceholder) return false;
            return maskPlaceholder[position] === character;
        };
        this.isCharacterFillingPosition = (character, position)=>{
            const options = this.maskOptions;
            if (!options) return false;
            const { mask } = options;
            if (!character || position >= mask.length) return false;
            const item = mask[position];
            return item.editable ? item.rule.test(character) : item.char === character;
        };
        this.isPositionEditable = (position)=>{
            const options = this.maskOptions;
            if (!options) return false;
            const item = options.mask[position];
            return void 0 !== item && item.editable;
        };
        this.isValueEmpty = (value)=>value.split('').every((character, position)=>!this.isPositionEditable(position) || !this.isCharacterFillingPosition(character, position));
        this.isValueFilled = (value)=>{
            const options = this.maskOptions;
            if (!options) return false;
            return this.getFilledLength(value) === options.lastEditablePosition + 1;
        };
        this.getDefaultSelectionForValue = (value)=>{
            const filledLength = this.getFilledLength(value);
            const cursorPosition = this.getRightEditablePosition(filledLength);
            return {
                start: null != cursorPosition ? cursorPosition : 0,
                end: null != cursorPosition ? cursorPosition : 0
            };
        };
        this.getFilledLength = (value)=>{
            const characters = value.split('');
            const lastFilledIndex = findLastIndex(characters, (character, position)=>this.isPositionEditable(position) && this.isCharacterFillingPosition(character, position));
            return lastFilledIndex + 1;
        };
        this.getStringFillingLengthAtPosition = (string, position)=>{
            const characters = string.split('');
            const insertedValue = characters.reduce((value, character)=>this.insertCharacterAtPosition(value, character, value.length), repeat(' ', position));
            return insertedValue.length - position;
        };
        this.getLeftEditablePosition = (position)=>{
            for(let i = position; i >= 0; i--)if (this.isPositionEditable(i)) return i;
            return null;
        };
        this.getRightEditablePosition = (position)=>{
            const options = this.maskOptions;
            if (!options) return null;
            const { mask } = options;
            for(let i = position; i < mask.length; i++)if (this.isPositionEditable(i)) return i;
            return null;
        };
        this.formatValue = (value)=>{
            const options = this.maskOptions;
            if (!options) return value;
            const { maskPlaceholder, mask, literals } = options;
            if (!maskPlaceholder) {
                value = this.insertStringAtPosition('', value, 0);
                while(value.length < mask.length && !this.isPositionEditable(value.length))value += literals[value.length];
                return value;
            }
            return this.insertStringAtPosition(maskPlaceholder, value, 0);
        };
        this.clearRange = (value, start, len)=>{
            if (!len) return value;
            const end = start + len;
            const options = this.maskOptions;
            if (!options) return value;
            const { maskPlaceholder, literals } = options;
            const clearedValue = value.split('').map((character, i)=>{
                const isEditable = this.isPositionEditable(i);
                if (!maskPlaceholder && i >= end && !isEditable) return '';
                if (i < start || i >= end) return character;
                if (!isEditable) return literals[i];
                if (maskPlaceholder) return maskPlaceholder[i];
                return '';
            }).join('');
            return this.formatValue(clearedValue);
        };
        this.insertCharacterAtPosition = (value, character, position)=>{
            const options = this.maskOptions;
            if (!options) return value;
            const { mask, maskPlaceholder, literals } = options;
            if (position >= mask.length) return value;
            const isAllowed = this.isCharacterAllowedAtPosition(character, position);
            const isEditable = this.isPositionEditable(position);
            const nextEditablePosition = this.getRightEditablePosition(position);
            const isNextPlaceholder = maskPlaceholder && nextEditablePosition ? character === maskPlaceholder[nextEditablePosition] : null;
            const valueBefore = value.slice(0, position);
            if (isAllowed || !isEditable) {
                const insertedCharacter = isAllowed ? character : literals[position];
                value = valueBefore + insertedCharacter;
            }
            if (!isAllowed && !isEditable && !isNextPlaceholder) value = this.insertCharacterAtPosition(value, character, position + 1);
            return value;
        };
        this.insertStringAtPosition = (value, string, position)=>{
            const options = this.maskOptions;
            if (!options) return value;
            const { mask, maskPlaceholder, literals } = options;
            if (!string || position >= mask.length) return value;
            const characters = string.split('');
            const isFixedLength = this.isValueFilled(value) || !!maskPlaceholder;
            const valueAfter = value.slice(position);
            value = characters.reduce((val, character)=>this.insertCharacterAtPosition(val, character, val.length), value.slice(0, position));
            if (isFixedLength) value += valueAfter.slice(value.length - position);
            else if (this.isValueFilled(value)) value += literals.slice(value.length).join('');
            else {
                const editableCharactersAfter = valueAfter.split('').filter((character, i)=>this.isPositionEditable(position + i));
                value = editableCharactersAfter.reduce((val, character)=>{
                    const nextEditablePosition = this.getRightEditablePosition(val.length);
                    if (null === nextEditablePosition) return val;
                    if (!this.isPositionEditable(val.length)) val += literals.slice(val.length, nextEditablePosition).join('');
                    return this.insertCharacterAtPosition(val, character, val.length);
                }, value);
            }
            return value;
        };
        this.processChange = (currentState, previousState)=>{
            var _previousSelection_start, _selection_start, _selection_end;
            const options = this.maskOptions;
            const { value, selection } = currentState;
            const previousValue = previousState.value;
            const previousSelection = previousState.selection;
            let newValue = value;
            let enteredString = '';
            let formattedEnteredStringLength = 0;
            let removedLength = 0;
            const previousStart = null != (_previousSelection_start = previousSelection.start) ? _previousSelection_start : 0;
            const selectionStart = null != (_selection_start = selection.start) ? _selection_start : 0;
            const selectionEnd = null != (_selection_end = selection.end) ? _selection_end : 0;
            let cursorPosition = Math.min(previousStart, selectionStart);
            if (selectionEnd > previousStart) {
                enteredString = newValue.slice(previousStart, selectionEnd);
                formattedEnteredStringLength = this.getStringFillingLengthAtPosition(enteredString, cursorPosition);
                if (formattedEnteredStringLength) {
                    var _previousSelection_length;
                    removedLength = null != (_previousSelection_length = previousSelection.length) ? _previousSelection_length : 0;
                } else removedLength = 0;
            } else if (newValue.length < previousValue.length) removedLength = previousValue.length - newValue.length;
            if (!options) return {
                value: previousValue,
                enteredString,
                selection: {
                    start: cursorPosition,
                    end: cursorPosition
                }
            };
            const { mask, prefix, lastEditablePosition } = options;
            newValue = previousValue;
            if (removedLength) {
                var _previousSelection_length1;
                if (1 === removedLength && !(null != (_previousSelection_length1 = previousSelection.length) ? _previousSelection_length1 : 0)) {
                    var _this_getRightEditablePosition, _this_getLeftEditablePosition;
                    const deleteFromRight = previousStart === selectionStart;
                    cursorPosition = deleteFromRight ? null != (_this_getRightEditablePosition = this.getRightEditablePosition(selectionStart)) ? _this_getRightEditablePosition : 0 : null != (_this_getLeftEditablePosition = this.getLeftEditablePosition(selectionStart)) ? _this_getLeftEditablePosition : 0;
                }
                newValue = this.clearRange(newValue, cursorPosition, removedLength);
            }
            newValue = this.insertStringAtPosition(newValue, enteredString, cursorPosition);
            cursorPosition += formattedEnteredStringLength;
            if (cursorPosition >= mask.length) cursorPosition = mask.length;
            else if (prefix && cursorPosition < prefix.length && !formattedEnteredStringLength) cursorPosition = prefix.length;
            else if (cursorPosition >= prefix.length && cursorPosition < lastEditablePosition && formattedEnteredStringLength) {
                var _this_getRightEditablePosition1;
                cursorPosition = null != (_this_getRightEditablePosition1 = this.getRightEditablePosition(cursorPosition)) ? _this_getRightEditablePosition1 : cursorPosition;
            }
            newValue = this.formatValue(newValue);
            return {
                value: newValue,
                enteredString,
                selection: {
                    start: cursorPosition,
                    end: cursorPosition
                }
            };
        };
        this.maskOptions = parse_mask(options);
    }
}
export default MaskUtils;
